import { useState } from 'react';
import { useProject } from '../../hooks/useProject';
import { usePreProcessingMetrics } from '../../hooks/usePreProcessingMetrics';
import { hasPermission } from '../../types/project';
import { broadcastBasicSearch } from '../../services/angular-bridge';
import { fieldContains } from '../../utilities/searchSyntax';
import { ProcessingReportModal } from './ProcessingReportModal';
import { PieChartPanel } from '../../components/charts/PieChartPanel';
import { LiquidGauge } from '../../components/charts/LiquidGauge';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { DownloadButton } from '../../components/DownloadButton';

type DataSelection = 'Extension' | 'Category';

interface Props {
  projectId: string;
}

export function PreProcessingTab({ projectId }: Props) {
  const { data: project, isLoading: isLoadingProject } = useProject(projectId);
  const canManageReports = project ? hasPermission(project, 'MANAGE_REPORTS') : false;
  const { data: metrics, isLoading: isLoadingMetrics } = usePreProcessingMetrics(projectId);
  const [selection, setSelection] = useState<DataSelection>('Extension');
  const [showReport, setShowReport] = useState(false);

  const collectionCount = project?.numCollections ?? 0;
  const custodianCount = project?.custodians?.length ?? 0;
  const sourceSize = metrics?.projectSourceSize ?? 0;
  const outputSize = (metrics?.projectSourceSize ?? 0) + (metrics?.projectOutputSize ?? 0);
  const gaugeTotal = Math.max(sourceSize, outputSize);

  const sizeData = selection === 'Extension' ? (metrics?.extensionBySize ?? []) : (metrics?.typeBySize ?? []);
  const countData = selection === 'Extension' ? (metrics?.extensionByCount ?? []) : (metrics?.typeByCount ?? []);
  const sizeTitle = selection === 'Extension' ? 'Extension by Size' : 'Category by Size';
  const countTitle = selection === 'Extension' ? 'Extension by Count' : 'Category by Count';

  function handleSearch(label: string) {
    broadcastBasicSearch(fieldContains(selection === 'Extension' ? 'fileExtension' : 'category', label));
  }

  if (isLoadingProject || isLoadingMetrics) return <LoadingPlaceholder />;

  return (
    <>
      <div className="clearfix">
        <h2 className="pull-left">
          <i className="far fa-lightbulb" aria-hidden="true" />{' '}
          {project?.name} Overview
        </h2>
        {canManageReports && (
          <DownloadButton onClick={() => setShowReport(true)} testId="project-home-pre-processing-report-btn" />
        )}
      </div>

      <p className="lead">
        This Project contains {collectionCount} Collections and {custodianCount} Custodians
      </p>

      <div className="row">
        <div className="col-md-5 text-center">
          <h2>Pre-Processing</h2>
          <LiquidGauge value={sourceSize} total={gaugeTotal} />
        </div>
        <div className="col-md-2 text-center hidden-xs" style={{ marginTop: 150, marginLeft: -25 }}>
          <i className="fas fa-arrow-right" style={{ fontSize: 128, opacity: 0.07 }} aria-hidden="true" />
        </div>
        <div className="col-md-2 visible-xs" style={{ marginLeft: 110, marginTop: 20 }}>
          <i className="fas fa-arrow-down" style={{ fontSize: 128, opacity: 0.07 }} aria-hidden="true" />
        </div>
        <div className="col-md-5 text-center">
          <h2>Post-Processing</h2>
          <LiquidGauge value={outputSize} total={gaugeTotal} />
        </div>
      </div>

      <div style={{ marginTop: 150 }}>
        <div className="row">
          <h2>Data Overview</h2>
          <div className="btn-group">
            {(['Extension', 'Category'] as DataSelection[]).map(s => (
              <label
                key={s}
                className={`btn btn-default${selection === s ? ' active' : ''}`}
                onClick={() => setSelection(s)}
                data-testid="project-home-pre-processing-data-btn"
              >
                {s}
              </label>
            ))}
          </div>

          {metrics && (
            <div style={{ marginTop: 44 }}>
              <PieChartPanel key={`size-${selection}`} title={sizeTitle} data={sizeData} valueType="bytes" onItemClick={handleSearch} />
              <div className="col-md-2" />
              <PieChartPanel key={`count-${selection}`} title={countTitle} data={countData} valueType="count" onItemClick={handleSearch} />
            </div>
          )}
        </div>
      </div>
      {showReport && (
        <ProcessingReportModal projectId={projectId} projectName={project?.name ?? ''} type="pre_processing_report"
          onClose={() => setShowReport(false)} />
      )}
    </>
  );
}
